import clsx from "clsx";
import _ from "lodash";
import { FormLabel } from "react-bootstrap";
import {
  type Control,
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
  type UseControllerProps,
} from "react-hook-form";

import { useScopedController } from "@/components/ScopedForm/hooks";
import RHErrorMessage from "@/containers/RHErrorMessage";
import TTFormGroup from "@/containers/TTFormGroup";
import TTTextarea, { type RefPropsInterface } from "@/containers/TTTextarea";

interface PropsType<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
>
  extends UseControllerProps<TFieldValues, TName, TTransformedValues>, Omit<RefPropsInterface, "name"> {
  control: Control<TFieldValues, any, TTransformedValues>;

  validate?: boolean;
  maxLength?: number;
  isValid?: boolean;
  isInvalid?: boolean;
  spellCheck?: boolean;

  hideError?: boolean;
  namespace?: string;
  errorClassName?: string;
  containerClassName?: string;

  label?: string;
  labelClassName?: string;

  fallBackOnNull?: boolean;
}

export default function RHTextarea<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
>({
  validate = true,
  isValid,
  isInvalid,
  id,
  hideError = false,
  namespace,
  errorClassName,
  containerClassName,
  label,
  labelClassName,
  fallBackOnNull,
  ...props
}: PropsType<TFieldValues, TName, TTransformedValues>) {
  const {
    field,
    formState: { isSubmitted },
    fieldState: { invalid, isTouched, error },
  } = useScopedController(_.omit(props, ["disabled"]));

  const klass = clsx("form-control", {
    "is-valid": isValid ?? (validate && !invalid && (isTouched || isSubmitted)),
    "is-invalid": isInvalid ?? (validate && invalid && (isTouched || isSubmitted)),
  });

  let value = field.value;
  if (fallBackOnNull && value === null) {
    value = "" as unknown as FieldPathValue<TFieldValues, TName>;
  }

  if (label && id) {
    return (
      <TTFormGroup className={containerClassName}>
        <FormLabel htmlFor={id} className={labelClassName}>
          {label}
        </FormLabel>

        <TTTextarea id={id} className={klass} {...props} {...field} value={value} />

        {validate && !hideError && (
          <RHErrorMessage namespace={namespace} errorMessage={error} className={errorClassName} />
        )}
      </TTFormGroup>
    );
  }

  return (
    <>
      <TTTextarea id={id} className={klass} {...props} {...field} value={value} />

      {validate && !hideError && (
        <RHErrorMessage namespace={namespace} errorMessage={error} className={errorClassName} />
      )}
    </>
  );
}
